import { SxProps, Tooltip, Theme, SvgIconProps } from "@mui/material";
import { AutoAwesome, OpenInNew, Person } from "@mui/icons-material";
import { ComponentType, useState } from "react";
import CloudUpload from "@mui/icons-material/CloudUpload";
import { useAppSelector } from "redux/hooks";
import { selectIsSupport } from "redux/slices/session";
import { FieldValueGroupSource, FormField } from "generated/graphql";
import SimpleMenu, { SimpleMenuItem } from "components/SimpleMenu";
import { selectClientLens } from "redux/slices/clientLens";
import AddIcon from "@mui/icons-material/Add";
import { TestCaseDialog } from "components/TestCaseDialog";
import { stripNull } from "helpers/coll";
import { FormFieldValueGroupUI } from "Backend";

function getIconDataFromSourcetype(sourceType: string): [ComponentType<SvgIconProps>, string] {
  switch (sourceType) {
    case "llm":
    case "audiogram_genie":
    case "structuredFormatExtraction":
    case "structuredFormatExtraction_table":
      return [AutoAwesome, "Autoreview"];
    case "excelImport":
      return [CloudUpload, "Imported from Excel"];
    default:
      return [Person, "Manual"];
  }
}

const MANUAL_SOURCE: FieldValueGroupSource = {
  sourceType: "manual",
};

interface SourceTypeIconProps extends SvgIconProps {
  valueGroupSource?: FieldValueGroupSource;
  formField?: FormField;
  sx?: SxProps<Theme>;
  hideTooltip?: boolean;
  valueGroup?: FormFieldValueGroupUI;
  className?: string;
}

export function SourceTypeIcon({ valueGroupSource, hideTooltip, formField, valueGroup, ...rest }: SourceTypeIconProps) {
  const amISupport = useAppSelector(selectIsSupport);
  const lensId = useAppSelector(selectClientLens).id;
  const [testCaseDialogOpen, setTestCaseDialogOpen] = useState(false);
  const sourceAiChatId = stripNull(valueGroupSource?.sourceAiChatId);
  const canGenerateTestCase = sourceAiChatId !== undefined && formField !== undefined && valueGroup !== undefined;

  const coalescedSource = valueGroupSource || MANUAL_SOURCE;

  const [IconClass, tooltip] = getIconDataFromSourcetype(coalescedSource.sourceType);
  const openChatInNewTab = () => {
    const chatId = sourceAiChatId;
    const path = `/lens-editor/${lensId}/ai/chats/${chatId}`;
    window.open(window.location.origin + path, "_blank", "noopener,noreferrer");
  };

  const items: SimpleMenuItem[] = [
    { name: "Open Chat", type: "action", action: openChatInNewTab, icon: <OpenInNew /> },
  ];

  if (canGenerateTestCase) {
    items.push({ name: "New Test Case", type: "action", action: () => setTestCaseDialogOpen(true), icon: <AddIcon /> });
  }

  if (coalescedSource.sourceAiChatId && amISupport) {
    return (
      <>
        <SimpleMenu
          customIcon={<IconClass {...rest} />}
          items={items}
          iconButtonProps={{ disableRipple: true, className: "p-0 flex items-center" }}></SimpleMenu>
        {testCaseDialogOpen && canGenerateTestCase && (
          <TestCaseDialog
            lensId={lensId}
            aiChatId={sourceAiChatId}
            formField={formField}
            open={testCaseDialogOpen}
            close={() => setTestCaseDialogOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <Tooltip title={!hideTooltip && tooltip}>
      <IconClass {...rest} />
    </Tooltip>
  );
}

export default SourceTypeIcon;
